import { AbstractViewModel } from '../../../utils/viewmodels/viewmodel';
import { useViewModel } from '../../../utils/viewmodels/useViewModel';
import type { ChatAvatarUser } from '../../chatbot.types';
import type { AvatarVariant, ChatUserAvatarState } from './chat-user-avatar.state';

export class ChatUserAvatarViewModel extends AbstractViewModel<ChatUserAvatarState> {
  static initialState: ChatUserAvatarState = {
    variant: { type: 'silhouette' },
  };

  private user: ChatAvatarUser;

  constructor(user: ChatAvatarUser) {
    super(ChatUserAvatarViewModel.initialState);
    this.user = user;
    this.resolve();
    this.onGravatarError = this.onGravatarError.bind(this);
  }

  onGravatarError() {
    this.setVariant(this.computeFallback());
  }

  setVariant = (variant: AvatarVariant): void => {
    this.update((state) => {
      state.variant = variant;
    });
  };

  private async resolve() {
    const isAnon = this.user.isAnonymous ?? true;
    const email = this.user.email;
    if (email && !isAnon) {
      try {
        const url = await this.buildGravatarUrl(email);
        this.setVariant({ type: 'gravatar', url });
        return;
      } catch {
        // Gravatar hash failed — fall through to fallback
      }
    }
    this.setVariant(this.computeFallback());
  }

  private computeFallback(): AvatarVariant {
    const displayName = this.user.displayName ?? null;
    const email = this.user.email ?? null;

    if (displayName) {
      const initials = ChatUserAvatarViewModel.getInitials(displayName);
      if (initials) return { type: 'initials', text: initials };
    }
    if (email) return { type: 'initials', text: email[0].toUpperCase() };
    return { type: 'silhouette' };
  }

  static getInitials(value: string): string {
    const parts = value.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return parts[0]?.[0]?.toUpperCase() ?? '';
  }

  private async buildGravatarUrl(email: string): Promise<string> {
    const hash = await ChatUserAvatarViewModel.hashEmail(email);
    return `https://www.gravatar.com/avatar/${hash}?s=80&d=404`;
  }

  static async hashEmail(email: string): Promise<string> {
    const trimmed = email.trim().toLowerCase();
    const buffer = new TextEncoder().encode(trimmed);
    const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
    return Array.from(new Uint8Array(hashBuffer))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  }
}

export const useChatUserAvatarViewModel = (user: ChatAvatarUser) =>
  useViewModel<ChatUserAvatarState, ChatUserAvatarViewModel>(() => new ChatUserAvatarViewModel(user));
